// Desktop stacked cards CSS styles for AddEditLoadDialog
export const stackedCardsStyles = `
  /* Smooth scrolling for dialog content */
  [data-radix-dialog-content] {
    scroll-behavior: smooth !important;
  }

  .stacked-cards-container {
    position: relative;
    padding: 0;
    margin: 0 auto;
    width: fit-content;
  }

  /* ===== Smooth entrance animations ===== */
  @keyframes dialogFadeUp {
    0% { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @keyframes contentReveal {
    0% { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @keyframes fieldSlideIn {
    0% { opacity: 0; transform: translateX(-6px); }
    100% { opacity: 1; transform: translateX(0); }
  }

  @keyframes progressPop {
    0% { opacity: 0; transform: scale(0.7); }
    60% { transform: scale(1.08); }
    100% { opacity: 1; transform: scale(1); }
  }

  /* Dialog content entrance */
  .load-dialog-enter {
    animation: dialogFadeUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* Section content reveal - desktop only */
  .section-content-enter {
    animation: contentReveal 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* Staggered field animations - desktop only */
  .field-stagger > * {
    opacity: 0;
    animation: fieldSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  .field-stagger > *:nth-child(1) { animation-delay: 0.03s; }
  .field-stagger > *:nth-child(2) { animation-delay: 0.06s; }
  .field-stagger > *:nth-child(3) { animation-delay: 0.09s; }
  .field-stagger > *:nth-child(4) { animation-delay: 0.12s; }
  .field-stagger > *:nth-child(5) { animation-delay: 0.15s; }
  .field-stagger > *:nth-child(6) { animation-delay: 0.18s; }
  .field-stagger > *:nth-child(7) { animation-delay: 0.21s; }
  .field-stagger > *:nth-child(8) { animation-delay: 0.24s; }
  .field-stagger > *:nth-child(9) { animation-delay: 0.27s; }
  .field-stagger > *:nth-child(10) { animation-delay: 0.30s; }

  /* Progress icon entrance - desktop only */
  .progress-icon-enter {
    animation: progressPop 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* ===== MOBILE: Single cohesive entrance ===== */
  @keyframes mobileSlideUp {
    0% { opacity: 0; transform: translateY(40px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @keyframes mobileCardReveal {
    0% { opacity: 0; transform: scale(0.96) translateY(10px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }

  @keyframes mobileFadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }

  @keyframes mobileDotsIn {
    0% { opacity: 0; transform: scale(0.5); }
    100% { opacity: 1; transform: scale(1); }
  }

  /* Mobile wrapper - everything slides up as one unit */
  .mobile-load-enter {
    animation: mobileSlideUp 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* Mobile header fades in quickly */
  .mobile-header-enter {
    animation: mobileFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both;
  }

  /* Mobile progress dots - subtle pop */
  .mobile-progress-enter {
    animation: mobileFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
  }

  .mobile-dot-enter {
    opacity: 0;
    animation: mobileDotsIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* Mobile card - smooth scale + fade */
  .mobile-card-enter {
    animation: mobileCardReveal 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both;
  }

  /* Mobile carousel track - smooth slide from right */
  .mobile-carousel-track {
    transition: transform 0.5s ease-out !important;
    will-change: transform;
  }
  .mobile-carousel-track.swiping {
    transition: none !important;
  }

  /* Mobile slide indicators entrance */
  .mobile-indicators-enter {
    animation: mobileFadeIn 0.3s ease 0.25s both;
  }

  /* Progress bar styles */
  .desktop-progress-bar {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 8px 0 16px;
    width: clamp(400px, 45vw, 800px);
  }

  .progress-icon-btn {
    width: clamp(28px, 2.5vw, 40px);
    height: clamp(28px, 2.5vw, 40px);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
  }

  .progress-icon-btn.complete { background: var(--color-completed, #d4845a); color: white; }
  .progress-icon-btn.enabled { background: var(--color-next-enabled, #e09570); color: white; }
  .progress-icon-btn.locked { background: var(--color-locked-bg, #d0d4d8); color: var(--color-locked-text, #9ca3ab); cursor: not-allowed; }
  .progress-icon-btn.current { transform: scale(1.15); box-shadow: 0 0 0 3px white, 0 0 0 5px var(--color-active, #d4845a); }

  .progress-connector { width: clamp(16px, 1.5vw, 28px); height: 2px; background: #e2e8f0; flex-shrink: 0; }
  .progress-connector.done { background: var(--color-completed, #d4845a); }

  /* Main layout - card left, tabs right */
  .card-tabs-layout {
    display: flex;
    align-items: stretch;
    gap: 0;
    position: relative;
    padding-left: 48px;
  }

  /* Active card - left side - responsive sizing */
  .active-section-card {
    width: clamp(380px, 38vw, 680px);
    min-width: clamp(380px, 38vw, 680px);
    max-width: clamp(380px, 38vw, 680px);
    height: clamp(450px, 65vh, 750px);
    background: var(--color-active-card-bg, linear-gradient(135deg, #fce4d6 0%, #fdf0e9 100%));
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid var(--color-active-card-border, #f0c9b3);
    box-shadow: 0 8px 32px rgba(180, 120, 80, 0.1), 0 2px 8px rgba(0,0,0,0.04);
    position: relative;
    z-index: 10;
    animation: cardSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    display: flex;
    flex-direction: column;
  }

  @keyframes cardSlideIn {
    0% { opacity: 0; transform: translateX(-16px) scale(0.97); }
    100% { opacity: 1; transform: translateX(0) scale(1); }
  }

  .active-card-header {
    padding: 22px 32px 10px;
    border-bottom: 1px solid var(--color-active-card-border, #f0c9b3);
    background: var(--color-active-header-bg, linear-gradient(135deg, #f5cbb8 0%, #fce4d6 100%));
  }

  .active-card-number {
    font-family: 'Georgia', 'Times New Roman', serif;
    font-style: italic;
    font-size: 34px;
    font-weight: 400;
    color: var(--color-active-number, #c89070);
    line-height: 1;
    margin-bottom: 2px;
  }

  .active-card-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--color-active-title, #5c3a24);
    margin: 0;
    letter-spacing: -0.3px;
  }

  .active-card-content {
    padding: 16px 24px 24px;
    flex: 1;
    overflow-y: auto;
  }

  .active-card-content label {
    color: #475569 !important;
  }

  .active-card-content::-webkit-scrollbar { width: 5px; }
  .active-card-content::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
  .active-card-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  /* Right-side cascading tabs */
  .right-tabs-area {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    padding: 16px 0;
    margin-left: -12px;
    z-index: 5;
  }

  .right-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px 10px 24px;
    border-radius: 0 14px 14px 0;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);
    white-space: nowrap;
    border: 1px solid transparent;
    border-left: none;
    position: relative;
    min-width: 160px;
  }

  /* Blinking animation for next tab indicator */
  @keyframes tabBlink {
    0%, 100% { 
      opacity: 1; 
      transform: translateX(0);
      box-shadow: 0 0 0 2px transparent;
    }
    50% { 
      opacity: 0.85; 
      transform: translateX(4px);
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5);
    }
  }

  .right-tab.tab-blink {
    animation: tabBlink 1.2s ease-in-out infinite !important;
  }

  .right-tab:hover:not(.tab-locked) {
    transform: translateX(6px);
    box-shadow: 2px 2px 12px rgba(0,0,0,0.08);
  }

  .right-tab.tab-locked {
    cursor: not-allowed;
    opacity: 0.8;
  }

  .right-tab svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }

  .right-tab .tab-check {
    margin-left: auto;
    font-size: 13px;
  }

  .right-tab .tab-lock {
    margin-left: auto;
  }

  .right-tab .tab-lock svg {
    width: 12px;
    height: 12px;
    opacity: 0.5;
  }

  /* Compact layout for smaller/zoomed viewports */
  @media (max-width: 1400px) {
    .desktop-progress-bar {
      gap: 6px;
      width: clamp(350px, 50vw, 600px);
    }
    
    .progress-icon-btn {
      width: 28px;
      height: 28px;
    }
    
    .progress-connector {
      width: 12px;
    }
    
    .card-tabs-layout {
      padding-left: 24px;
    }
    
    .active-section-card {
      width: 420px;
      min-width: 420px;
      max-width: 420px;
      height: 480px;
    }
    
    .active-card-header {
      padding: 16px 24px 8px;
    }
    
    .active-card-number {
      font-size: 28px;
    }
    
    .active-card-title {
      font-size: 15px;
    }
    
    .active-card-content {
      padding: 12px 18px 18px;
    }
    
    .right-tabs-area {
      gap: 4px;
    }
    
    .right-tab {
      padding: 8px 12px 8px 18px;
      font-size: 11px;
      min-width: 120px;
      border-radius: 0 10px 10px 0;
    }
    
    .right-tab svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Action buttons container - responsive */
  .action-buttons-container {
    width: clamp(380px, 38vw, 680px);
    margin-left: 48px;
  }
  
  @media (max-width: 1400px) {
    .action-buttons-container {
      width: 420px;
      margin-left: 24px;
    }
  }
`;

// Inject styles into DOM
export function injectStyles() {
  if (typeof document !== 'undefined') {
    const styleId = 'stacked-cards-styles';
    // Always update styles (remove check to force refresh during development)
    let styleElement = document.getElementById(styleId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    styleElement.textContent = stackedCardsStyles;
  }
}

injectStyles();
